import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { ApiAccessTableService } from '@/components/pages/settings/integrations/ApiAccessTable/services/ApiAccessTableService';

import useUserPublicTokens from '@/utils/hooks/swr/integrations/useUserPublicTokens';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { HOMEPAGE } from '@/lib/routes/privateRoutes';

import LinkBase from '@/components/base/LinkBase';
import { Illustration } from '@/components/composite/Illustration';
import TableBody from '@/components/pages/settings/integrations/ApiAccessTable/TableBody';
import TablePlaceholder from '@/components/pages/settings/integrations/ApiAccessTable/TablePlaceholder';
import { Trans } from '@/components/tools/translation';

import illustrationError from './../../../../../../public/images/errors/403.png';
import illustrationEn from './../../../../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from './../../../../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from './../../../../../../public/images/illustrations/illustrations-empty-fr.webp';

function ErrorWrapper({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center rounded-md bg-icoGray-100 pb-2 dark:bg-icoDarkMode-deepBlack">
            <Illustration
                width={300}
                height={300}
                className="-ml-24 max-w-[300px]"
                src={{
                    en: illustrationError,
                    fr: illustrationError,
                    es: illustrationError
                }}
            />
            <span className="max-w-md text-center">{children}</span>
        </div>
    );
}

function ApiAccessTable() {
    const { t } = useTranslation('integrations');
    const { user } = useUser();
    const { isLoading, userPublicTokens, mutateUserPublicTokens, userPublicTokensError } =
        useUserPublicTokens();

    const table = useReactTable({
        data: userPublicTokens ?? [],
        columns: ApiAccessTableService.getColumns(t),
        meta: {
            date_format: user.settings.date_format,
            timezone: user.settings.timezone
        },
        getCoreRowModel: getCoreRowModel()
    });

    if (userPublicTokensError) {
        if (userPublicTokensError.status === 403) {
            return (
                <ErrorWrapper>
                    <Trans
                        i18nKey="integrations:api_access.access_not_allowed"
                        components={{
                            tag0: <LinkBase href={HOMEPAGE} className="text-icoBlue underline" />
                        }}
                    />
                </ErrorWrapper>
            );
        }
        return (
            <ErrorWrapper>
                <Trans
                    i18nKey="errors:error_global_unexpected_occured"
                    components={{
                        tag0: (
                            <button
                                onClick={() => mutateUserPublicTokens(undefined)}
                                className="underline"
                            />
                        )
                    }}
                />
            </ErrorWrapper>
        );
    }

    if (!userPublicTokens?.length && !isLoading) {
        return (
            <div className="flex flex-1 flex-col items-center justify-center rounded-md bg-icoGray-100 pb-2 dark:bg-icoDarkMode-deepBlack">
                <Illustration
                    width={300}
                    height={300}
                    className="max-w-[300px]"
                    src={{ en: illustrationEn, fr: illustrationFr, es: illustrationEs }}
                />
                <span>{t('api_access.no_api_keys')}</span>
            </div>
        );
    }

    return (
        <div className="scrollbar">
            <table className="w-full border-separate border-spacing-y-1">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className="whitespace-nowrap px-6 pb-5 pt-0 text-left first:pl-12 last:pr-12">
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </th>
                            ))}
                            <th />
                        </tr>
                    ))}
                </thead>
                {isLoading ? <TablePlaceholder table={table} /> : <TableBody table={table} />}
            </table>
        </div>
    );
}

export default ApiAccessTable;
